package com.swetdot.swetgram.ui.text

import androidx.compose.runtime.Composable
import org.jetbrains.compose.resources.PluralStringResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/**
 * A message a pure rule has CHOSEN, before anybody has decided what words it is made of.
 *
 * WHY THIS TYPE EXISTS AT ALL. `stringResource` is a `@Composable`, and roughly half the copy in
 * this module is picked by something that is not one: view models, and the pure `when` tables that
 * sit beside them so they can be tested without a renderer. `bannerText`, `muteStatusLine`,
 * `groupFeedEmptyBody`, `actionDescription`, `freshnessLabel` and their kin all decide WHICH
 * sentence applies from state the composition cannot see. Moving those into a composable to reach
 * the resource loader would drag the decision back inside the renderer and end the ability to test
 * it, which is the property those functions were pulled out to get.
 *
 * `StringResource` and `PluralStringResource` are ORDINARY VALUES in commonMain -- only the
 * `stringResource()` / `pluralStringResource()` LOOKUPS are composable -- so a pure function may
 * name one and hand it back. That is the whole seam: the rule picks the identity, the composition
 * resolves the words.
 *
 * THIS IS AN IMPROVEMENT AND NOT A TAX, and the tests are where it shows. Before this type, the
 * only way to assert that `groupFeedEmptyBody` treats an admins-only group differently from one
 * nobody could read was to compare English prose, so every copy edit broke a test and the cheapest
 * repair was to loosen the assertion. Now a test names the key. A copy edit changes nothing a test
 * can see; a rule change fails loudly. And a test can no longer accidentally pin a SENTENCE that
 * has since become false, which is the exact failure DECISIONS.md §21 records: a passing test
 * defended the claim that a suspended account "can still sign back in" for as long as that claim
 * was wrong.
 *
 * WHAT IS DELIBERATELY MISSING is a `Raw(String)` case. It would be the obvious convenience and it
 * would immediately become the hole every un-localised sentence escapes through, and nothing
 * mechanical could tell an un-translated literal from a legitimately dynamic one. Text that really
 * is dynamic -- a username, a formatted clock time, a display name -- travels as a FORMAT ARGUMENT
 * instead, where it belongs: those are values, not copy.
 */
sealed interface UiText {

    /**
     * One string, with optional format arguments.
     *
     * [args] are substituted positionally into `%1$s` .. `%n$s`. Compose Resources calls
     * `toString()` on each, so a number arrives as a number and a nested [UiText] would arrive as
     * its own debug representation -- which is why [resolve] unwraps nested [UiText] arguments
     * first rather than letting them through.
     */
    data class Res(val id: StringResource, val args: List<Any> = emptyList()) : UiText

    /**
     * One string chosen by a COUNT, which is not the same thing as a string with a number in it.
     *
     * [count] selects the grammatical form and [args] supplies what is printed, and keeping them
     * apart is the point rather than an accident. Discover's "30+ new posts" is the case that
     * forces it: the figure on screen is the string `30+`, while the form of the noun after it is
     * governed by 30. Russian needs `постов` there and `поста` at 22, and no amount of care at the
     * call site can produce that by concatenation.
     *
     * Nothing here may be built with `if (count == 1)`. English has two forms, Russian has three
     * for integers (1 участник, 2 участника, 5 участников, and 21 back to участник), and the rule
     * that picks between them lives in CLDR and in the resource file, never in Kotlin.
     */
    data class Plural(
        val id: PluralStringResource,
        val count: Int,
        val args: List<Any> = emptyList(),
    ) : UiText

    /**
     * Several messages run together with a fixed separator.
     *
     * NEEDED, not a convenience. A group's subtitle is "Public group · @alex · 12 members", and its
     * last part is a plural: a plural cannot be an argument to a format string, because the
     * argument is substituted after the form has been chosen and the choice needs the count. So the
     * composition is a node of its own.
     *
     * The separator is punctuation rather than copy -- " · " and ", " read the same in every
     * language this app is planned for (SPS §17.3) -- so it stays a literal. A language that needs
     * a different one needs a different rule, not a different string.
     */
    data class Joined(val parts: List<UiText>, val separator: String) : UiText
}

/** Sugar for the overwhelmingly common no-argument case. */
fun StringResource.text(): UiText = UiText.Res(this)

/** Sugar for a string with arguments, so call sites do not spell `listOf` every time. */
fun StringResource.text(vararg args: Any): UiText = UiText.Res(this, args.toList())

/**
 * Sugar for a plural whose only argument is the count itself, which is most of them.
 *
 * The count is passed TWICE on purpose: once to choose the form and once to be printed. Compose
 * Resources' two-argument `pluralStringResource` does not substitute the quantity into the string,
 * so a `%1$s` that nobody filled would render literally.
 */
fun PluralStringResource.count(count: Int): UiText = UiText.Plural(this, count, listOf(count))

/** A plural whose printed figure is not simply its count. See [UiText.Plural]. */
fun PluralStringResource.count(count: Int, vararg args: Any): UiText =
    UiText.Plural(this, count, args.toList())

/**
 * The words, at last.
 *
 * The ONE place in the app that turns a chosen message into a rendered sentence. Recursive, so a
 * [UiText.Joined] resolves its parts and a nested [UiText] argument resolves before it is
 * substituted.
 *
 * `for` rather than `joinToString`: the transform lambda of a stdlib function is not composable, so
 * the loop is not a style choice.
 */
@Composable
fun UiText.resolve(): String = when (this) {
    is UiText.Res -> {
        val resolved = resolveArgs(args)
        if (resolved.isEmpty()) stringResource(id) else stringResource(id, *resolved)
    }

    is UiText.Plural -> {
        val resolved = resolveArgs(args)
        if (resolved.isEmpty()) {
            pluralStringResource(id, count)
        } else {
            pluralStringResource(id, count, *resolved)
        }
    }

    is UiText.Joined -> {
        val rendered = ArrayList<String>(parts.size)
        for (part in parts) rendered.add(part.resolve())
        rendered.joinToString(separator)
    }
}

/** The nullable rules -- `muteStatusLine`, `actionDescription`, `freshnessLabel` -- resolve too. */
@Composable
fun UiText?.resolveOrNull(): String? = this?.resolve()

@Composable
private fun resolveArgs(args: List<Any>): Array<Any> {
    if (args.isEmpty()) return emptyArray()
    val out = ArrayList<Any>(args.size)
    for (arg in args) out.add(if (arg is UiText) arg.resolve() else arg)
    return out.toTypedArray()
}
